import {
  CcfBox,
  CcfButton,
  CcfCustomerIcon,
  CcfHappySentimentIcon,
  CcfNeutralSentimentIcon,
  CcfPersonIcon,
  CcfSadSentimentIcon,
  CcfSupervisorVoiceIcon,
  CcfTypography,
} from '@nice-ccf/ui-controls';
import styles from './ccf-copilot-auto-summary.module.scss';
import { Divider, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { copilotAutoSummaryData } from '../mock-data/copilot-mock-data';
import { activeContactSelector } from '../../../ccf-inbox/ccf-inbox.slice';
import {
  setIsCopilotSummarySubmitted,
  updateLoadAutoSummary,
} from '../ccf-copilot-chat/+state/ccf-copilot.slice';

/* eslint-disable-next-line */
export interface CcfCopilotAutoSummaryProps {
  copilotAutoSummary: copilotAutoSummaryData;
}

/**
 * CcfCopilotAutoSummary - used to display Copilot Auto Summary section in Copilot tab
 * @param props -?- CcfCopilotAutoSummaryProps
 * @example <CcfCopilotAutoSummary />
 */
export function CcfCopilotAutoSummary(props: CcfCopilotAutoSummaryProps) {
  const { copilotAutoSummary } = props;
  const dispatch = useDispatch();
  const activeContact = useSelector(activeContactSelector);

  /**
   * Function is executed to submit auto summary and hungup the contact
   * @example submitAutoSummaryHandler()
   */
  function renderEventSourceIcon(source: string) {
    if (source === 'customer') {
      return <CcfCustomerIcon viewBox=" 0 0 26 22" />;
    } else {
      return <CcfSupervisorVoiceIcon fillcolor="#526B7A" />;
    }
  }

  /**
   * Function is executed to submit auto summary and hungup the contact
   * @example submitAutoSummaryHandler()
   */
  const submitAutoSummaryHandler = () => {
    dispatch(setIsCopilotSummarySubmitted(true));
    setTimeout(() => {
      dispatch(updateLoadAutoSummary(false));
    }, 3000);
  };

  return (
    <>
      <CcfBox
        id={`agent_videocall_copilot_autosummary_mainContainer`}
        className={styles.overallSummaryBlock}
      >
        <CcfBox  id="agent_videocall_copilot_autosummary_overallSummaryRating_div" className={styles.summaryBlock}>
          <CcfBox
            id="agent_videocall_copilot_autosummary_overallSummaryRating_text"
            className={styles.summaryTitleRow}
            mb={1.5}
          >
            <CcfTypography className={styles.summaryTitle}>
              Overall Summary Rating
            </CcfTypography>
            {copilotAutoSummary.overallSummaryRating === 'Positive' ? (
              <CcfHappySentimentIcon
                id={`agent_videocall_copilot_autosummary_positiveSentiment`}
                className={styles.summaryIcon}
              />
            ) : copilotAutoSummary.overallSummaryRating === 'Negative' ? (
              <CcfSadSentimentIcon
                id={`agent_videocall_copilot_autosummary_negativeSentiment`}
                className={styles.summaryIcon}
              />
            ) : copilotAutoSummary.overallSummaryRating === 'Neutral' ? (
              <CcfNeutralSentimentIcon
                id={`agent_videocall_copilot_autosummary_neutralSentiment`}
                className={styles.summaryIcon}
              />
            ) : null}
          </CcfBox>
          {activeContact?.media === 'VideoCall' && (
            <CcfBox
              id="agent_videocall_copilot_autosummary_lastContact_div"
              className={styles.summaryTitleRow}
              mb={1.5}
            >
              <CcfTypography
                id="agent_videocall_copilot_autosummary_lastContact_text"
                className={styles.highlightedSummaryTxt}
              >
                Last Contact
              </CcfTypography>
              <CcfTypography
                id={`agent_videocall_copilot_autosummary_lastContact_${copilotAutoSummary.lastContact}`}
                className={styles.highlightedSummaryTxt}
              >
                {copilotAutoSummary.lastContact}
              </CcfTypography>
            </CcfBox>
          )}
          {activeContact?.media === 'VideoCall' && (
            <CcfBox
              id="agent_videocall_copilot_autosummary_sentiment_div"
              className={styles.summaryTitleRow}
            >
              <CcfTypography
                id="agent_videocall_copilot_autosummary_sentiment_text"
                className={styles.highlightedSummaryTxt}
              >
                Sentiment
              </CcfTypography>
              {copilotAutoSummary.sentiment === 'Positive' ? (
                <CcfHappySentimentIcon
                  id={`agent_videocall_copilot_sentiment_positive`}
                  className={styles.summaryIcon}
                />
              ) : copilotAutoSummary.sentiment === 'Negative' ? (
                <CcfSadSentimentIcon
                  id={`agent_videocall_copilot_sentiment_negative`}
                  className={styles.summaryIcon}
                />
              ) : copilotAutoSummary.sentiment === 'Neutral' ? (
                <CcfNeutralSentimentIcon
                  id={`agent_videocall_copilot_sentiment_neutral`}
                  className={styles.summaryIcon}
                />
              ) : null}
            </CcfBox>
          )}
        </CcfBox>
        <Divider sx={{ borderBottomWidth: 3 }} />

        <CcfBox
          id="agent_videocall_copilot_outcome_div"
          className={styles.summaryBlock}
        >
          <CcfTypography
            id="agent_videocall_copilot_outcome_text"
            className={styles.summaryTitle}
            mb={1.5}
          >
            Outcome
          </CcfTypography>
          <CcfTypography
            id={`agent_videocall_copilot_outcome_${copilotAutoSummary.outcome}`}
            className={styles.highlightedSummaryTxt}
          >
            {copilotAutoSummary.outcome}
          </CcfTypography>
        </CcfBox>

        <Divider sx={{ borderBottomWidth: 3 }} />

        <CcfBox
          id="agent_videocall_copilot_contactReason_div"
          className={styles.summaryBlock}
        >
          <CcfTypography
            id="agent_videocall_copilot_contactReason_text"
            className={styles.summaryTitle}
            mb={1.5}
          >
            Contact Reason
          </CcfTypography>
          <CcfTypography
            id={`agent_videocall_copilot_contactReason_${copilotAutoSummary.contactReason}`}
            className={styles.highlightedSummaryTxt}
          >
            {copilotAutoSummary.contactReason}
          </CcfTypography>
        </CcfBox>

        <Divider sx={{ borderBottomWidth: 3 }} />

        <CcfBox
          className={styles.summaryBlock}
          id={`agent_videocall_copilot_events_div`}
        >
          <CcfBox mb={1.5}>
            <CcfTypography
              id={`agent_videocall_copilot_events_text`}
              className={styles.summaryTitle}
            >
              Events
            </CcfTypography>
          </CcfBox>

          {copilotAutoSummary.events.map((event: any) => {
            return (
              <CcfBox
              id={`agent_videocall_copilot_events_${event?.eventText}`}
                className={styles.listItem}
              >
                {renderEventSourceIcon(event?.eventSource)}
                <CcfTypography
                  
                  className={
                    event?.eventText === 'Schedule follow up'
                      ? styles.followUpTxt
                      : styles.listItemText
                  }
                  style={{
                    marginTop:
                      activeContact?.media === 'VideoCall' ? '-10px' : '0px',
                  }}
                >
                  {event?.eventText}
                </CcfTypography>
              </CcfBox>
            );
          })}
        </CcfBox>
        <Divider sx={{ borderBottomWidth: 3 }} />
        <CcfBox
          id={`agent_videocall_copilot_additionalNotes_container`}
          className={styles.summaryBlock}
        >
          <CcfBox>
            <CcfTypography
              id={`agent_videocall_copilot_additionalNotes_text`}
              className={styles.summaryTitle}
              mb={1.5}
            >
              Additional Notes
            </CcfTypography>
            <CcfBox
              id={`agent_videocall_copilot_additionalNotes_answer`}
              className={styles.notesBlock}
            >
              <CcfTypography className={styles.notesContentTxt}>
                {copilotAutoSummary.additionalNotes}
              </CcfTypography>
            </CcfBox>
          </CcfBox>
        </CcfBox>
      </CcfBox>
      <CcfBox
        id={`agent_videocall_copilot_submit_button_div`}
        className={styles.submitBtnBlock}
      >
        <CcfButton
          primary
          className={styles.submitBtn}
          id={`agent_videocall_copilot_submit_button`}
        >
          <CcfTypography
            // onClick={submitAutoSummaryHandler}
            className={styles.submitBtnText}
          >
            Submit
          </CcfTypography>
        </CcfButton>
      </CcfBox>
    </>
  );
}

export default CcfCopilotAutoSummary;
